import {
  ProviderOrdersIncotermsEnum,
  ProviderOrdersShipDirectionEnum,
  RelationEnum,
  RoleEnum,
  WarehouseInstructionEnum,
} from '__generated__/types';
import { IMoney } from '@xometry/money';
import { IMoldProperties } from 'components/Deal/shared/Positions/PositionPartData/moldPartFieldsTypes';
import { IAttachedFile } from 'interfaces';
import { Dayjs } from 'utils/dayjs';

import { AuditState, MeasurementProtocols, PartRelatedObject } from './PartsStore';
import { TagContexts } from './TagStore';

export interface IJobStatisitcs {
  autoAccepted: boolean;
  applicationCount: number;
  irrelevantCount: number;
  counterofferCount: number;
  seenCount: number;
  offersCount: number;
}

export interface OwnerIdOption {
  value: number;
  label: string;
  name: string;
  key: number;
  imageUrl?: string | null;
}

export interface AcceptOfferValues {
  internalComment?: string;
  createDraftPo?: boolean;
  notifyProvider?: boolean;
  closeJob?: boolean;
  sendRejectEmails?: boolean;
  withoutProviderResponse: boolean;
}

export interface CancelJobValues {
  reject?: boolean;
  templateType?: string;
  decisionRefusalReason?: string;
  notifyRejectedPartners?: boolean;
}

export interface NewJobFormValues {
  dealId: number;
  dealPipedriveId?: string;
  ownerId?: number | null;
  ownerIdOption?: OwnerIdOption;
  strategyId?: number;

  state: 'draft' | 'active' | 'placed' | 'expired' | 'canceled';
  leadtime?: string;
  leadtimeMoment?: Dayjs;

  publicationStart?: string;
  publicationEnd?: string;
  positions: JobPositionsByKey;

  allowDirectShipping?: boolean;
  allowAutoConfirmation?: boolean;
  allowCounterofferFrom?: string;
  showPositionPrices?: boolean;
  urgentJob?: boolean;

  publicComment?: string;
  internalComment?: string;
  selectedPartners: number[];
}

export interface JobIntercompanyRecord {
  objectType: string;
  objectId: number;
  createdAt: Date;
  updatedAt: Date;
  dealId: number;
  id: string;
  role?: RoleEnum | null;
  relationType?: RelationEnum | null;
  foreignObjectId?: string | null;
  foreignObjectType: string;
  foreignRegion: string;
  foreignDealId?: string | null;
  foreignRole?: RoleEnum | null;
  foreignObjectNumber?: string | null;
}

export interface IJob extends NewJobFormValues {
  id: number;
  global: boolean;
  jobNumber: string;
  partnersFilter: any;
  selectedPartners: number[];
  value: number;
  valueCurrency?: string;
  isHeavyweight: boolean;
  customerLotsValue: number;
  customerLotsValueCurrency?: string;
  statistics: IJobStatisitcs;
  strategyContent: IContent;
  strategy: {
    id: number;
    name: string;
  };
  intercompanyRecords?: JobIntercompanyRecord[];
  updatedAt: string;
}

export interface JobPartData {
  id: number;
  name: string;

  material?: string;
  postProcessing?: string;
  customerNote?: string;
  productionRemark?: string;

  position?: number;
  isPreQuote?: boolean;
  preQuotedByLabel?: string;
  preQuotedComment?: string;
  measurementProtocolNeeded?: MeasurementProtocols;
  isExpress: boolean;
  internalComment?: string;

  samplesQuantity?: number;
  samplesComment?: string;
  isSamplesNeeded?: boolean;

  auditState?: AuditState;
  auditReviewRemark?: string;

  files?: IAttachedFile[];
  tags?: IShiftTag[];
  relatedObjects?: PartRelatedObject[];
  moldParts: number[];
  partType: string;
  moldProperties?: IMoldProperties | null;
}

export interface IJobPosition {
  id?: number;
  _destroy?: boolean;
  key: string;
  lotId: number;
  partId: number;
  name: string;
  part?: JobPartData;
  material?: string;
  postProcessing?: string;
  partDetails?: string;
  quantity: number;
  customerPrice: number;
  customerPriceCurrency?: string;
  providerPrice: number;
  price: number;
  priceCurrency?: string;
}

export interface JobPositionsByKey {
  [key: string]: IJobPosition;
}

export interface RankType {
  delayedPosInProgressQuantity?: number | null;
  timing: number | null;
  overdueRatio: number | null;
  quality: number | null;
  qualityRatio: number | null;
  packing: number | null;
  communication: number | null;
  total: number | null;
  poQuantity: number | null;
  cancelRatio: number | null;
}

export interface IJobProvider {
  id: number;
  state: string;
  name: string;
  primaryContact?: {
    firstName: string;
    lastName?: string;
    name?: string;
    email?: string;
  };
  decoratedId: string;
  displayName: string;
  phone: string;
  email: string;
  rank?: RankType;
  billingAddress: {
    country: string;
  };
  shippingAddress: {
    city?: string;
    country?: string;
    zip?: string;
    address?: string;
  } | null;
  website: string;
  inProductionPos: {
    id: number;
    price: number;
    priceMoney?: {
      amount: string;
      amountCents: number;
      currencyCode: string;
    };
  }[];
}

export interface IShiftTag {
  id: number;
  name: string;
  parentId?: number;
  context:
    | TagContexts.Materials
    | 'restricted_methods'
    | TagContexts.ProductionMethods
    | TagContexts.ProductionMethodFeatures
    | 'industries'
    | 'certifications'
    | 'qualities'
    | 'languages'
    | 'partner_segments';
}

export interface IWorkspacesJobOffersTotalPriceType {
  counterofferPriceMargin: number;
  counterofferPriceMoney: IMoney;
  offerPriceMargin: number;
  offerPriceMoney: IMoney;
}

export interface IJobOffer {
  id: number;
  jobId: number;
  updatedAt: string;
  currency?: string;
  dapCostsApprox?: string;
  providerId: number;
  provider: IJobProvider;
  providerMatchedTags: IShiftTag[];
  providerMissingTags: IShiftTag[];
  providerRestrictedTags: IShiftTag[];
  value: string;
  leadtime?: string;
  logisticCostEstMoney: IMoney;
  totalPriceAfterLogistics: IWorkspacesJobOffersTotalPriceType;
  totalPriceBeforeLogistics: IWorkspacesJobOffersTotalPriceType;
  label?: string;
  bonus?: string;
  totalValue?: string;
  publicationStart: string;
  publicationEnd?: string;
  notificationSentAt?: string;
  readOnly?: boolean;
  allowCounterofferFrom?: string;
  allowDirectShipping?: boolean;
  allowAutoConfirmation?: boolean;
  showPositionPrices?: boolean;
  viewedAt?: string;
  respondedAt?: string;
  responseState: 'empty' | 'application' | 'counteroffer' | 'irrelevant';
  responseComment: string;
  responseRefusalReason: string;
  zipUrl?: string;
  decisionState: 'pending' | 'auto_accepted' | 'accepted' | 'rejected' | 'rejected_by_partner';
  decidedAt?: string;
  decidedBy?: {
    id: number;
    name: string;
  };
  decisionRefusalReason: string;
  counterofferLeadtime?: string;
  counterofferValue?: string;
  zendeskTicketId: string;
  providerOrderId: number;
  providerOrderNumber: string;
  internalComment?: string;
  publicComment: null | string;
  isUrgent?: boolean;
  icOcPoDeadlineDiffDays?: number;
  margin?: string;
  customerLotsValue?: {
    cents: number;
  };

  shipIncoterms?: ProviderOrdersIncotermsEnum | null;
  shipIncotermsCity?: string | null;
  shipIncotermsByPlaneOnly?: boolean | null;
  shipDirection?: ProviderOrdersShipDirectionEnum | null;
  shipAllowDirectLegal?: boolean | null;
  shipAllowDirectLegalXomSales?: boolean | null;
  shipAllowDirectOrder?: boolean | null;
  shipAllowDirectProvider?: boolean | null;
  shipDirectionToEndCustomer?: boolean | null;
  shipQcRequired?: boolean | null;
  shipWarehouse?: WarehouseInstructionEnum | null;
  shippingInstructionsUpdatedAt?: string | null;
  shippingInstructionsUpdatedBy?: { name: string } | null;
}

export interface ISupportTicket {
  id: string;
  subject: string;
  comment: string;
}

export type IContentValueType =
  | Date
  | boolean
  | string
  | number
  | string[]
  | Record<string, unknown>
  | undefined
  | number[];

export type IContentInputType =
  | 'text'
  | 'approval'
  | 'provider_ref'
  | 'part_ref'
  | 'qc_ref'
  | 'po_ref'
  | 'coupon_ref'
  | 'sh_ref'
  | 'cl_ref'
  | 'su_ref'
  | 'salesforce_case_ref'
  | 'number'
  | 'dropzone'
  | 'checkbox'
  | 'money'
  | 'select'
  | 'separator'
  | 'date'
  | 'datetime'
  | 'longtext'
  | 'url'
  | 'hidden'
  | 'event_ref'
  | 'claim_ref';

export interface IContent {
  [key: string]: IContentItem;
}

export interface IContentItem {
  key?: string;
  label: string;
  type: IContentInputType;
  value?: IContentValueType;
  options?: {
    label: string;
    value: any;
  }[];
  required?: boolean;
}

export interface IDealLot {
  id: number;
  partId: number;
  quantity: number;
  productionRemark: string;
  customerPrice: number;
  customerPriceCurrency?: string;
  providerPrice: number;
  providerPriceCurrency?: string;
  isClaimed?: boolean;
  files: IAttachedFile[];
  tags: IShiftTag[];
  isPlaced: boolean;
  isUsed: boolean;
  part: JobPartData;
}

export enum ClonePartnersList {
  EXCEPT_RESPONDED = 'except_responded',
  EMPTY = 'empty',
  ALL = 'all',
}

export interface CloneJobProps {
  setPublicationInterval?: boolean;
  partnersList: ClonePartnersList;
  total?: number;
}
